/**
 * Exercise Library Page
 * 
 * Filterable grid of exercise videos organized by category.
 * Users can filter by exercise type (mobility, strength, stretching).
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import SEO, { breadcrumbs } from '../components/common/SEO'
import PageHero from '../components/common/PageHero'
import ExerciseCard from '../components/common/ExerciseCard'
import WaveSeparator from '../components/common/WaveSeparator'
import { exercises, exerciseCategories } from '../data/exercises'
import { BOOKING_URL } from '../data/site'

const ExerciseLibraryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')

  // Filter exercises by category
  const filteredExercises = selectedCategory === 'all'
    ? exercises
    : exercises.filter(exercise => exercise.category === selectedCategory)

  return (
    <>
      <SEO
        title="Exercise Library — Mobility & Rehab Videos"
        description="Free guided videos covering joint mobility (CARs), strength, and stretching from Recreo Rehab's Toronto RMT team. Support your recovery between sessions."
        path="/exercise-library"
        keywords="exercise library Toronto, joint mobility CARs, rehab exercises, stretching videos, RMT home exercises"
        noIndex
        structuredData={[
          breadcrumbs([
            { name: 'Home', path: '/' },
            { name: 'Exercise Library', path: '/exercise-library' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Exercise Library',
            url: 'https://recreorehab.com/exercise-library',
            description:
              'Free exercise videos for mobility, strength, and stretching from the Recreo Rehab team.',
            isPartOf: { '@id': 'https://recreorehab.com/#website' },
          },
        ]}
      />

      {/* Hero */}
      <div className="bg-cream-200">
        <PageHero 
          title="Exercise Library"
          subtitle="Movement guides for your recovery journey"
          size="md"
          texture="earth-grain"
        />
      </div>

      {/* Exercise Grid */}
      <section className="py-20 md:py-28 bg-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category Filters */}
          <motion.div 
            className="flex flex-wrap gap-3 mb-16 justify-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            {exerciseCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-5 py-2 font-sans text-xs tracking-[0.1em] uppercase rounded-full transition-all duration-200 border ${
                  selectedCategory === category.id
                    ? 'bg-brand-orange-500 text-white border-brand-orange-500'
                    : 'bg-transparent text-dark-500 border-dark-300 hover:border-brand-orange-400 hover:text-brand-orange-500'
                }`}
              >
                {category.name}
              </button>
            ))}
          </motion.div>

          {/* Exercises Count */}
          <p className="text-center font-body text-dark-500 mb-10 italic text-base">
            Showing {filteredExercises.length} exercise{filteredExercises.length !== 1 ? 's' : ''}
          </p>

          {/* Exercises Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {filteredExercises.map((exercise) => (
              <ExerciseCard key={exercise.id} exercise={exercise} />
            ))}
          </div>

          {/* Empty State */}
          {filteredExercises.length === 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <p className="font-body text-dark-400 text-lg italic">
                No exercises found in this category.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 md:py-28 bg-brand-green-700 relative overflow-hidden">
        {/* Subtle noise texture overlay */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-script text-brand-orange-500 italic text-lg block mb-4">
            Custom Care
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium mb-6">
            Need Personalized Guidance?
          </h2>
          <p className="font-sans text-cream-200/90 text-sm md:text-base leading-relaxed mb-10 max-w-2xl mx-auto">
            Our therapists can create a customized exercise plan tailored to your specific needs 
            and goals. Book an assessment to get started.
          </p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-dark-700 px-8 py-3.5 rounded-full font-sans text-[11px] font-medium tracking-[0.15em] uppercase hover:bg-cream-100 transition-colors shadow-sm"
          >
            Book an Assessment
          </a>
        </div>
      </section>

    </>
  )
}

export default ExerciseLibraryPage
